import React from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy, Send, Check, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TorneiPrivati = () => {
  const [form, setForm] = React.useState({
    name: "",
    phone: "",
    email: "",
    preferred_date: "",
    players: "",
    message: "",
    event_type: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Inserisci il tuo nome");
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 5) {
      toast.error("Inserisci un numero di telefono valido");
      return;
    }
    setLoading(true);
    try {
      const userMsg = form.message.trim();
      const combinedMessage = form.event_type
        ? `Tipo evento: ${form.event_type}${userMsg ? ". " + userMsg : ""}`
        : userMsg || null;
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        preferred_date: form.preferred_date.trim() || null,
        players: form.players ? parseInt(form.players, 10) : null,
        message: combinedMessage,
      };
      await axios.post(`${API}/tournament-request`, payload);
      setSuccess(true);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm({
        name: "",
        phone: "",
        email: "",
        preferred_date: "",
        players: "",
        message: "",
        event_type: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Errore nell'invio. Riprova o chiamaci al 333 604 3477.";
      toast.error(typeof msg === "string" ? msg : "Errore nell'invio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="torneo-privato"
      className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10 overflow-hidden"
      data-testid="torneo-privato-section"
    >
      <Toaster position="top-center" theme="dark" richColors />
      <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[#CCFF00] opacity-[0.04] blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <Trophy size={16} className="text-[#CCFF00]" />
            <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
              Torneo Privato
            </span>
          </div>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
            Organizza il tuo<br />torneo a Padel Elvo.
          </h2>
          <p className="mt-6 text-white/60 font-dm text-base md:text-lg max-w-md">
            Compleanni, aziendali, tornei tra amici o tra club: pensiamo a tutto noi.
            Raccontaci la tua idea e ti ricontattiamo con una proposta su misura —
            campi, bar e premi inclusi.
          </p>
          <ul className="mt-8 space-y-3 font-dm text-sm text-white/70">
            <li className="flex items-center gap-3" data-testid="torneo-priv-perk-1">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Fino a 3 campi contemporanei
            </li>
            <li className="flex items-center gap-3" data-testid="torneo-priv-perk-2">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Gestione iscrizioni e tabelloni
            </li>
            <li className="flex items-center gap-3" data-testid="torneo-priv-perk-3">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Buffet, taglieri e aperitivi al bar
            </li>
            <li className="flex items-center gap-3" data-testid="torneo-priv-perk-4">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Premi personalizzati su richiesta
            </li>
          </ul>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 relative border border-white/15 bg-gradient-to-br from-[#171717] to-[#0A0A0A] p-8 md:p-10"
          data-testid="torneo-privato-form"
        >
          <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">
            Richiedi informazioni
          </div>
          <h3 className="mt-2 font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl leading-none">
            Raccontaci il tuo torneo
          </h3>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Nome e Cognome *" htmlFor="name">
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={update("name")}
                className="pe-input"
                placeholder="Mario Rossi"
                data-testid="torneo-input-name"
              />
            </Field>
            <Field label="Telefono *" htmlFor="phone">
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                className="pe-input"
                placeholder="333 604 3477"
                data-testid="torneo-input-phone"
              />
            </Field>
            <Field label="Email" htmlFor="email">
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="pe-input"
                placeholder="mario@esempio.it"
                data-testid="torneo-input-email"
              />
            </Field>
            <Field label="Data preferita" htmlFor="preferred_date">
              <input
                id="preferred_date"
                type="text"
                value={form.preferred_date}
                onChange={update("preferred_date")}
                className="pe-input"
                placeholder="Es. Sabato 15 giugno"
                data-testid="torneo-input-date"
              />
            </Field>
            <Field label="Numero giocatori" htmlFor="players">
              <input
                id="players"
                type="number"
                min="2"
                max="64"
                value={form.players}
                onChange={update("players")}
                className="pe-input"
                placeholder="Es. 16"
                data-testid="torneo-input-players"
              />
            </Field>
            <Field label="Tipo di evento" htmlFor="type_hint" className="md:col-span-1">
              <select
                id="type_hint"
                className="pe-input"
                value={form.event_type}
                onChange={update("event_type")}
                data-testid="torneo-input-type"
              >
                <option value="">Seleziona...</option>
                <option value="Tra amici">Tra amici</option>
                <option value="Compleanno">Compleanno</option>
                <option value="Aziendale">Aziendale</option>
                <option value="Tra club">Tra club</option>
                <option value="Altro">Altro</option>
              </select>
            </Field>
            <Field label="Messaggio" htmlFor="message" className="md:col-span-2">
              <textarea
                id="message"
                rows={4}
                value={form.message}
                onChange={update("message")}
                className="pe-input resize-none"
                placeholder="Raccontaci la tua idea: livello dei giocatori, orari, buffet, premi..."
                data-testid="torneo-input-message"
              />
            </Field>
          </div>

          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
            <p className="font-dm text-xs text-white/40 max-w-sm">
              Inviando la richiesta ci autorizzi a contattarti per organizzare il tuo torneo.
              Nessuno spam.
            </p>
            <button
              type="submit"
              disabled={loading || success}
              className="btn-primary px-7 py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="torneo-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Invio in corso...
                </>
              ) : success ? (
                <>
                  <Check size={16} />
                  Richiesta Inviata
                </>
              ) : (
                <>
                  Invia Richiesta
                  <Send size={16} />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, className = "", children }) => (
  <label htmlFor={htmlFor} className={`block ${className}`}>
    <span className="font-oswald uppercase tracking-[0.2em] text-[10px] text-white/50 block mb-2">
      {label}
    </span>
    {children}
  </label>
);
